<template>
  <d-container fluid class="main-content-container px-4">
    <!-- Page Header -->
    <d-row no-gutters class="page-header py-4">
      <d-col col sm="6" class="text-center text-sm-left mb-4 mb-sm-0">
        <span class="text-uppercase page-subtitle">{{ $t('service.services') }}</span>
        <h3 class="page-title">
          <template v-if="isIndentList">
            {{ $t('service.editIndentService') }}
          </template>
          <template v-else>
            {{ $t('service.editService') }}
          </template>
        </h3>
      </d-col>
      <d-col col sm="6" class="text-right text-sm-right d-flex justify-content-end align-items-end">
        <d-button
          theme="secondary"
          @click.prevent="$router.push(backUrl)"
          class="mr-1">
          <i class="material-icons">arrow_back</i>
          {{ $t('ui.backToList') }}
        </d-button>
        <d-button
          theme="success"
          @click.prevent="validateForm">
          {{ $t('ui.saveChanges') }}
        </d-button>
      </d-col>
    </d-row>

    <!-- Form -->
    <d-form>
      <d-card class="mb-5">
        <d-tabs v-sticky-tabs
          v-if="!loadError && allDataLoaded"
          card>
          <!-- Dane ogólne -->
          <d-tab
            :title="$t('ui.generalInfo')"
            active>
            <h4 class="mb-3 pb-3 border-bottom">
              {{ $t('ui.generalInfo') }}
            </h4>

            <validation-observer ref="service-edit-form" tag="form">
              <d-row>
                <d-col cols="4">
                  <div class="form-group">
                    <label for="name">
                      {{ $t('service.servicesGroup') }}:
                    </label>

                    <validation-provider
                      rules="required"
                      name="group-id"
                      v-slot="{ errors }">
                      <d-form-select
                        v-model="group_id"
                        :state="errors.length ? 'invalid' : null"
                        :options="groupOptions" />
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </d-col>

                <d-col cols="4">
                  <div class="form-group">
                    <label for="name">
                      {{ $t('service.serviceType') }}:
                    </label>

                    <validation-provider
                      rules="required|non_zero"
                      name="type-id"
                      v-slot="{ errors }">
                      <d-form-select
                        v-model="type_id"
                        :state="errors.length ? 'invalid' : null"
                        :options="typeOptions" />
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </d-col>

                <d-col cols="4">
                  <div class="form-group">
                    <label for="name">
                      {{ $t('service.fromHowManyPiecesThePriceIsMultiplied') }}:
                    </label>

                    <validation-provider
                      rules="required"
                      name="price-multiple-from"
                      v-slot="{ errors }">
                      <d-input
                        v-model="price_multiple_from"
                        type="number"
                        min="0"
                        step="1"
                        :state="errors.length ? 'invalid' : null" />
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </d-col>
              </d-row>

              <d-row v-if="isIndentList">
                <d-col cols="4">
                  <div class="form-group">
                    <label for="name">
                      {{ $t('service.daysToSample') }}:
                    </label>

                    <validation-provider
                      rules="required|min_value:0"
                      name="days-to-sample"
                      v-slot="{ errors }">
                      <d-input
                        v-model="days_to_sample"
                        type="number"
                        min="0"
                        step="1"
                        :state="errors.length ? 'invalid' : null" />
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </d-col>

                <d-col cols="4">
                  <div
                    v-if="isIndentList"
                    class="form-group">
                    <label for="days_no_sample_indent">
                      {{ $t('service.daysNoSampleIndent') }}
                    </label>

                    <validation-provider
                      rules="only-integer-numbers|min_value:0"
                      name="days_no_sample_indent"
                      v-slot="{ errors }">
                      <d-input-group
                        :prepend="$t('service.daysNoSampleIndentPrepend')"
                        :append="$t('service.daysNoSampleIndentAppend')"
                        :class="{
                          'w-100': true,
                          'is-invalid': errors.length
                        }">
                        <input
                          :class="{
                            'form-control': true,
                            'is-invalid': errors.length,
                            'text-center': true
                          }"
                          :invalid="errors.length ? true : null"
                          type="number"
                          v-model="days_no_sample_indent" />
                      </d-input-group>
                      <d-form-invalid-feedback>
                        {{ errors[0] }}
                      </d-form-invalid-feedback>
                    </validation-provider>
                  </div>
                </d-col>
              </d-row>

              <d-row>
                <d-col cols="8">
                  <div class="form-group">
                    <label for="name">
                      {{ $t('service.serviceName') }}:
                    </label>

                    <validation-provider
                      rules="required"
                      name="name"
                      v-slot="{ errors }">
                      <d-input
                        :state="errors.length ? 'invalid' : null"
                        v-model="name" />
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>

                  <div class="form-group">
                    <label for="name">
                      {{ $t('service.serviceCode') }}:
                    </label>

                    <validation-provider
                      rules="required"
                      name="code"
                      v-slot="{ errors }">
                      <d-input
                        :state="errors.length ? 'invalid' : null"
                        v-model="code" />
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>

                  <div class="form-group">
                    <label for="name">
                      {{ $t('service.catalogUrl') }}:
                    </label>

                    <validation-provider
                      rules="valid-url"
                      name="catalog_url"
                      v-slot="{ errors }">
                      <d-input
                        :state="errors.length ? 'invalid' : null"
                        v-model="catalog_url" />
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>

                  <div class="form-group">
                    <d-checkbox
                      inline
                      v-model="available_in_embriodery"
                      toggle
                      toggle-small
                      class="mt-2 mb-0">
                      {{ $t('service.availableInHafciarnia') }}
                    </d-checkbox>
                  </div>

                  <div class="form-group">
                    <d-checkbox
                      inline
                      v-model="disable_for_customer"
                      toggle
                      toggle-small
                      class="mt-2 mb-0">
                      {{ $t('product.disableForCustomer') }}
                    </d-checkbox>
                  </div>

                  <div class="form-group">
                    <label>
                      {{ $t('ui.shortDescription') }}:
                    </label>

                    <validation-provider
                      rules="required"
                      name="intro"
                      v-slot="{ errors }">
                      <d-input
                        :state="errors.length ? 'invalid' : null"
                        v-model="intro" />
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>

                  <div class="form-group">
                    <label>
                      {{ $t('service.serviceDescription') }}:
                    </label>

                    <validation-provider
                      rules="required"
                      name="description"
                      v-slot="{ errors }">
                      <d-textarea
                        :state="errors.length ? 'invalid' : null"
                        v-model="description"
                        :value.prop="description"
                        :rows="8"></d-textarea>
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </d-col>

                <d-col cols="4">
                  <d-card class="p-3">
                    <div class="form-group">
                      <label for="name">
                        {{ $t('service.stitchNumber') }}:
                      </label>

                      <d-row>
                        <d-col cols="6">
                          <d-input-group
                            :prepend="$t('ui.from')">
                            <d-input
                              type="number"
                              class="text-center"
                              v-model="stitch_number_from" />
                          </d-input-group>
                        </d-col>

                        <d-col cols="6">
                          <d-input-group
                            :prepend="$t('ui.to')">
                            <d-input
                              type="number"
                              class="text-center"
                              v-model="stitch_number_to" />
                          </d-input-group>
                        </d-col>
                      </d-row>
                    </div>

                    <div class="form-group">
                      <label for="name">
                        {{ $t('service.height') }}:
                      </label>

                      <d-row>
                        <d-col cols="6">
                          <d-input-group
                            :prepend="$t('ui.from')">
                            <d-input
                              type="number"
                              class="text-center"
                              v-model="height_from" />
                          </d-input-group>
                        </d-col>

                        <d-col cols="6">
                          <d-input-group
                            :prepend="$t('ui.to')">
                            <d-input
                              type="number"
                              class="text-center"
                              v-model="height_to" />
                          </d-input-group>
                        </d-col>
                      </d-row>
                    </div>
                  </d-card>
                </d-col>
              </d-row>
            </validation-observer>
          </d-tab>

          <d-tab :title="$t('service.servicePrices')">
            <h4 class="mb-3 pb-3 border-bottom">
              {{ $t('service.servicePrices') }}
            </h4>

            <table
              v-if="loadedPrices && !loadPricesError"
              class="table mt-2 mb-0 full-width">
              <thead class="bg-light">
                <tr>
                  <th scope="col" class="border-0">{{ $t('price.quantityFrom') }}</th>
                  <th scope="col" class="border-0">{{ $t('price.price') }}</th>
                  <th scope="col" class="border-0">{{ $t('settings.currency.currency') }}</th>
                </tr>
              </thead>
              <tbody v-if="loadedPrices">
                <tr
                  v-for="(price, index) of prices"
                  :key="'service-' + index">
                  <td class="align-middle">
                    {{ price.value_from }}
                  </td>
                  <td class="align-middle">
                    {{ Number(price.price).toFixed(2) }}
                  </td>
                  <td class="align-middle">
                    <template v-if="price.currency">
                      {{ price.currency.code }}
                    </template>
                  </td>
                </tr>
                <tr v-if="!prices.length">
                  <td colspan="3" class="text-center">
                    {{ $t('service.noPricesForThisService') }}&hellip;
                  </td>
                </tr>
              </tbody>
            </table>

            <div v-if="!loadedPrices" class="pb-3 pt-3 text-center">
              {{ $t('ui.dataLoading') }}&hellip;
            </div>

            <d-alert
              v-if="loadPricesError"
              show
              theme="warning">
              {{ $t('ui.dataLoadingFailed') }} <a href="javascript:window.location.reload();" class="alert-link">{{ $t('ui.refreshPage') }}</a> {{ $t('ui.toTryAgain') }}
            </d-alert>
          </d-tab>

          <d-tab :title="$t('service.setupPrices')">
            <h4 class="mb-3 pb-3 border-bottom">
              {{ $t('service.setupPrices') }}
            </h4>

            <table
              v-if="loadedSetupPrices && !loadSetupPricesError"
              class="table mt-2 mb-0 full-width">
              <thead class="bg-light">
                <tr>
                  <th scope="col" class="border-0">{{ $t('price.quantityTo') }}</th>
                  <th scope="col" class="border-0">{{ $t('price.price') }}</th>
                  <th scope="col" class="border-0">{{ $t('settings.currency.currency') }}</th>
                </tr>
              </thead>
              <tbody v-if="loadedSetupPrices">
                <tr
                  v-for="(price, index) of setupPrices"
                  :key="'service-' + index">
                  <td class="align-middle">
                    {{ price.value_from }}
                  </td>
                  <td class="align-middle">
                    {{ Number(price.price).toFixed(2) }}
                  </td>
                  <td class="align-middle">
                    <template v-if="price.currency">
                      {{ price.currency.code }}
                    </template>
                  </td>
                </tr>
                <tr v-if="!setupPrices.length">
                  <td colspan="3" class="text-center">
                    {{ $t('service.noSetupPricesForThisService') }}&hellip;
                  </td>
                </tr>
              </tbody>
            </table>

            <div v-if="!loadedSetupPrices" class="pb-3 pt-3 text-center">
              {{ $t('ui.dataLoading') }}&hellip;
            </div>

            <d-alert
              v-if="loadSetupPricesError"
              show
              theme="warning">
              {{ $t('ui.dataLoadingFailed') }} <a href="javascript:window.location.reload();" class="alert-link">{{ $t('ui.refreshPage') }}</a> {{ $t('ui.toTryAgain') }}
            </d-alert>
          </d-tab>

          <d-tab :title="$t('settings.transaltion.translations')">
              <h4 class="pb-3 mb-0 border-bottom d-flex">
                {{ $t('settings.transaltion.translations') }}

                <d-button
                  class="ml-auto"
                  theme="success"
                  @click.prevent="addTranslation">
                  <i class="material-icons">add</i>
                  {{ $t('settings.transaltion.addTranslation') }}
                </d-button>
              </h4>

              <table class="table mt-2 mb-0 full-width">
                <thead class="bg-light">
                  <tr>
                    <th scope="col" class="border-0" style="width: 150px;">{{ $t('ui.language') }}</th>
                    <th scope="col" class="border-0" style="width: calc(100% - 350px);">{{ $t('service.serviceName') }}</th>
                    <th scope="col" class="border-0" style="width: 200px;">{{ $t('ui.actions') }}</th>
                  </tr>
                </thead>
                <tbody v-if="service_translations.length">
                  <tr
                    v-for="(translation, index) of service_translations"
                    :key="'price-' + index">
                    <td class="align-middle">
                      {{ getLanguageByID(translation.language_id) }}
                    </td>
                    <td class="align-middle">
                      {{ translation.name }}
                    </td>
                    <td class="align-middle">
                      <d-button
                        theme="primary"
                        @click.prevent="editTranslation(translation)"
                        class="mr-2"
                        :title="$t('ui.edit')">
                        <i class="material-icons">edit</i>
                      </d-button>
                      <d-button
                        theme="danger"
                        :disabled="removeTranslationInProgress"
                        @click.prevent="removeTranslation(translation.id)"
                        :title="$t('ui.delete')">
                        <i class="material-icons">delete</i>
                      </d-button>
                    </td>
                  </tr>
                </tbody>
                <tbody v-else>
                  <tr>
                    <td colspan="3" class="text-center">
                      {{ $t('service.noTranslationsForThisService') }}&hellip;
                    </td>
                  </tr>
                </tbody>
              </table>
            </d-tab>
        </d-tabs>

        <div v-if="!allDataLoaded" class="pb-3 pt-3 text-center overlay-loading">
          {{ $t('ui.dataLoading') }}&hellip;
        </div>

        <d-alert
          v-if="loadError"
          show
          theme="warning">
          {{ $t('ui.dataLoadingFailed') }} <a href="javascript:window.location.reload();" class="alert-link">{{ $t('ui.refreshPage') }}</a> {{ $t('ui.toTryAgain') }}
        </d-alert>
      </d-card>
    </d-form>

    <service-translation-new-popup
      v-if="showAddTranslationPopup"
      :serviceID="this.id"
      :languages="languages" />

    <service-translation-edit-popup
      v-if="showEditTranslationPopup"
      :serviceID="this.id"
      :translationData="selectedTranslation"
      :languages="languages" />
  </d-container>
</template>

<script>
import FormUtils from './../../utils/FormUtils.js';
import ListUtils from './../../utils/ListUtils.js';
import ServiceTranslationNewPopup from './ServiceTranslationNewPopup';
import ServiceTranslationEditPopup from './ServiceTranslationEditPopup';

export default {
  name: 'service-edit',
  components: {
    'service-translation-new-popup': ServiceTranslationNewPopup,
    'service-translation-edit-popup': ServiceTranslationEditPopup
  },
  computed: {
    allDataLoaded () {
      return this.loadedData && this.loadedGroups && this.loadedLanguages && this.loadedTypes;
    },
    backUrl () {
      if (this.isIndentList) {
        return '/zdobienia-indent';
      }

      return '/zdobienia';
    },
    groupOptions () {
      return [{ value: 0, text: this.$t('service.serviceWithoutGroup') }].concat(this.groups.map(group => ({
        value: group.id,
        text: group.name
      })));
    },
    typeOptions () {
      return [{ value: 0, text: this.$t('service.selectServiceType') }].concat(this.types.map(type => ({
        value: type.id,
        text: type.name
      })));
    },
    isIndentList () {
      return this.$route.path.indexOf('zdobienia-indent') > -1;
    }
  },
  data() {
    return {
      id: 0,
      available_in_embriodery: false,
      disable_for_customer: false,
      group_id: 0,
      type_id: 0,
      name: '',
      code: '',
      intro: '',
      catalog_url: '',
      price_multiple_from: 0,
      days_to_sample: 4,
      description: '',
      loadedData: false,
      loadedGroups: false,
      loadedPrices: false,
      loadedSetupPrices: false,
      loadedTypes: false,
      loadedLanguages: false,
      loadError: false,
      loadPricesError: false,
      loadSetupPricesError: false,
      groups: [],
      prices: [],
      setupPrices: [],
      service_translations: [],
      types: [],
      showAddTranslationPopup: false,
      showEditTranslationPopup: false,
      selectedTranslation: 0,
      removeTranslationInProgress: false,
      stitch_number_from: 0,
      stitch_number_to: 0,
      height_from: 0,
      height_to: 0
    };
  },
  mounted () {
    let instanceFieldsToLoad = ['available_in_embriodery', 'disable_for_customer', 'group_id', 'type_id', 'name', 'code', 'price_multiple_from', 'service_translations', 'intro', 'description', 'catalog_url', 'stitch_number_from', 'stitch_number_to', 'height_from', 'height_to'];

    if (this.isIndentList) {
      instanceFieldsToLoad = ['available_in_embriodery', 'disable_for_customer', 'group_id', 'type_id', 'name', 'code', 'price_multiple_from', 'service_translations', 'intro', 'description', 'catalog_url', 'days_to_sample', 'days_no_sample_indent', 'stitch_number_from', 'stitch_number_to', 'height_from', 'height_to'];
    }

    FormUtils.loadItemData(this, {
      method: 'get',
      endpointBase: '/services/item/',
      instanceFields: instanceFieldsToLoad,
      loadedKey: 'loadedData',
      booleanFields: ['available_in_embriodery', 'disable_for_customer'],
      params: {
        with: ['serviceTranslations']
      }
    });

    this.loadAdditionalData();
    this.loadPrices();
    this.loadSetupPrices();
    this.loadLanguages();
    this.$bus.$on('service-translation-new-popup-close', this.closeAddTranslationPopup);
    this.$bus.$on('service-translation-edit-popup-close', this.closeEditTranslationPopup);
    this.$bus.$on('service-reload-translations', this.loadTranslations);
  },
  methods: {
    validateForm () {
      FormUtils.validate(this.$refs['service-edit-form'], this.submitForm);
    },
    submitForm () {
      let formDataToSend = {
        available_in_embriodery: +this.available_in_embriodery,
        disable_for_customer: +this.disable_for_customer,
        group_id: this.group_id,
        type_id: this.type_id,
        name: this.name,
        code: this.code,
        intro: this.intro,
        price_multiple_from: this.price_multiple_from,
        description: this.description,
        stitch_number_from: this.stitch_number_from,
        stitch_number_to: this.stitch_number_to,
        height_from: this.height_from,
        height_to: this.height_to,
        catalog_url: this.catalog_url
      };

      if (this.isIndentList) {
        formDataToSend.days_to_sample = this.days_to_sample;
        formDataToSend.days_no_sample_indent = +this.days_no_sample_indent;
      }

      FormUtils.submit({
        $http: this.$http,
        endpoint: '/services/update/' + this.id,
        formData: formDataToSend,
        successTitle: this.$t('service.updateServiceSuccessTitle'),
        successText: this.$t('service.updateServiceSuccessText'),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('service.updateServiceErrorText'),
        successAction: () => {
          this.loadPrices();
          this.loadSetupPrices();
        }
      });
    },
    loadPrices () {
      FormUtils.loadAdditionalData(this, {
        method: 'get',
        endpoint: '/services/prices/items',
        loadedKey: 'loadedPrices',
        outputKey: 'prices',
        errorKey: 'loadPricesError',
        params: {
          where: {
            service_id: this.id,
            is_setup: 0
          }
        }
      });
    },
    loadSetupPrices () {
      FormUtils.loadAdditionalData(this, {
        method: 'get',
        endpoint: '/services/prices/items',
        loadedKey: 'loadedSetupPrices',
        outputKey: 'setupPrices',
        errorKey: 'loadSetupPricesError',
        params: {
          where: {
            service_id: this.id,
            is_setup: 1
          }
        }
      });
    },
    loadAdditionalData () {
      FormUtils.loadAdditionalData(this, {
        method: 'get',
        endpoint: '/services/groups/items',
        loadedKey: 'loadedGroups',
        outputKey: 'groups',
        errorKey: 'loadError'
      });

      FormUtils.loadAdditionalData(this, {
        method: 'get',
        endpoint: '/services/types',
        loadedKey: 'loadedTypes',
        outputKey: 'types',
        errorKey: 'loadError'
      });
    },
    loadLanguages () {
      FormUtils.loadAdditionalData(this, {
        method: 'get',
        endpoint: '/languages/items',
        loadedKey: 'loadedLanguages',
        outputKey: 'languages',
        errorKey: 'loadError'
      });
    },
    getLanguageByID (languageID) {
      let filteredLanguages = this.languages.filter(language => language.id === languageID);

      if (filteredLanguages.length) {
        return filteredLanguages[0].name;
      }

      return '';
    },
    loadTranslations () {
      FormUtils.loadItemData(this, {
        method: 'get',
        endpointBase: '/services/item/',
        instanceFields: ['service_translations'],
        loadedKey: null,
        params: {
          with: ['serviceTranslations']
        }
      });
    },
    addTranslation () {
      this.showAddTranslationPopup = true;
    },
    editTranslation (translationData) {
      this.selectedTranslation = JSON.parse(JSON.stringify(translationData));
      this.showEditTranslationPopup = true;
    },
    closeAddTranslationPopup () {
      this.showAddTranslationPopup = false;
    },
    closeEditTranslationPopup () {
      this.showEditTranslationPopup = false;
    },
    removeTranslation (translationID) {
      this.removeTranslationInProgress = true;

      ListUtils.removeItem({
        $http: this.$http,
        endpointBase: '/services/translations/delete/',
        id: translationID,
        successTitle: this.$t('settings.transaltion.deleteTranslationSuccessTitle'),
        successText: this.$t('settings.transaltion.deleteTranslationSuccessText'),
        confirmTitle: this.$t('settings.transaltion.deleteTranslationConfirmTitle'),
        confirmText: this.$t('ui.deleteConfirmText'),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('settings.transaltion.deleteTranslationErrorText'),
        successAction: () => {
          this.removeTranslationInProgress = false;
          this.loadTranslations();
        },
        cancelAction: () => {
          this.removeTranslationInProgress = false;
        }
      });
    }
  },
  beforeDestroy () {
    this.$bus.$off('service-translation-new-popup-close', this.closeAddTranslationPopup);
    this.$bus.$off('service-translation-edit-popup-close', this.closeEditTranslationPopup);
    this.$bus.$off('service-reload-translations', this.loadTranslations);
  }
};
</script>
